/* eslint-disable react/prop-types */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Icon from '../Icon';
import { FONT_SYSTEM, tokens } from '../tokens';

// Question-mark help icon with a token-themed popover (gen-3). Opens on
// hover or focus, and toggles on click/tap for touch screens.
//
// The popover is fixed-positioned from the icon's viewport rect so it is
// never clipped by a scrolling table, and kept inside the viewport with a
// small gutter. Its type is reset explicitly: the icon usually sits inside
// an uppercase, letter-spaced, right-aligned header, none of which should
// leak into a sentence of help text.

const GUTTER = 12;
const OFFSET = 9;

export const popoverPosition = (rect, width, viewportWidth) => {
  const centred = rect.left + rect.width / 2 - width / 2;
  const maxLeft = Math.max(GUTTER, viewportWidth - width - GUTTER);
  return {
    top: rect.bottom + OFFSET,
    left: Math.min(Math.max(GUTTER, centred), maxLeft),
  };
};

const HelpTip = ({ content, size = 17, width = 300 }) => {
  const t = tokens(useTheme());
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const anchor = useRef(null);

  const boxWidth = Math.min(width, window.innerWidth - 2 * GUTTER);

  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    setPos(
      popoverPosition(
        anchor.current.getBoundingClientRect(),
        boxWidth,
        window.innerWidth
      )
    );
  }, [open, boxWidth]);

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={anchor}
        type="button"
        aria-label="What is this?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setOpen(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
          margin: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: t.faint,
        }}
      >
        <Icon name="help-circle" size={size} color="currentColor" />
      </button>
      {open && pos ? (
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: boxWidth,
            boxSizing: 'border-box',
            padding: '11px 14px',
            borderRadius: 12,
            background: t.card,
            border: '1px solid ' + t.border,
            boxShadow: t.shadowPop,
            color: t.text,
            fontFamily: FONT_SYSTEM,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.5,
            letterSpacing: 'normal',
            textTransform: 'none',
            textAlign: 'left',
            whiteSpace: 'normal',
            zIndex: 60,
          }}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
};

export default HelpTip;
